export const colors = {
  border: '#71767A',
  dividerStroke: '#71767A',
  borderLight: '#D5D5D5',
  disabledFill: '#E6E6E6',
  disabledText: '#858585',
  error: '#88041E',
  primary: '#2F54A5',
  optionHover: '#F2F2F2',
  optionSelected: 'rgb(213, 221, 237)',
  text: '#000000',
  muted: '#121A22',
  backdrop: 'rgba(0, 0, 0, 0.16)',
  iconFill: '#1C1B1F',
};

const fieldBorderRadius = '16px';
export const drawerDividerColor = colors.border;
export const drawerDividerWidth = '1px';
export const drawerBorderWidth = '1.5px';
export const drawerShadow = '-8px 0px 24px rgba(17, 17, 17, 0.06)';

const sharedFieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: fieldBorderRadius,
    backgroundColor: '#FFFFFF',
    minHeight: 44,
    fontFamily: 'Noto Sans',
    fontSize: 14,
    px: 0,
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: fieldBorderRadius,
      borderColor: colors.border,
      borderWidth: drawerBorderWidth,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.border,
      borderWidth: drawerBorderWidth,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.border,
      borderWidth: drawerBorderWidth,
    },
  },
  '& .MuiOutlinedInput-root fieldset': {
    borderRadius: fieldBorderRadius,
  },
  '& .MuiInputBase-input': {
    padding: '10px 16px',
    fontFamily: 'Noto Sans',
    fontSize: 14,
    lineHeight: '24px',
    color: colors.text,
  },
  '& .MuiInputBase-input::placeholder': {
    color: colors.text,
    opacity: 1,
  },
};

export const compactFieldSx = {
  ...sharedFieldSx,
  '& .MuiOutlinedInput-root': {
    ...sharedFieldSx['& .MuiOutlinedInput-root'],
    minHeight: 44,
    padding: '0px 16px',
  },
  '& .MuiInputBase-input': {
    ...sharedFieldSx['& .MuiInputBase-input'],
    padding: '0px',
    fontSize: 14,
    lineHeight: '21px',
  },
};

export const personIdFieldSx = {
  ...sharedFieldSx,
  '& .MuiOutlinedInput-root': {
    ...sharedFieldSx['& .MuiOutlinedInput-root'],
    height: 'var(--person-id-field-height, 132px)',
    minHeight: 132,
    alignItems: 'flex-start',
    overflow: 'hidden',
    boxSizing: 'border-box',
    padding: '16px',
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.primary,
      borderWidth: 2,
    },
    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.26)',
    },
  },
  '& .MuiInputBase-inputMultiline': {
    width: '100%',
    padding: '0 !important',
    margin: 0,
    fontFamily: 'Noto Sans',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '21px',
    letterSpacing: '0%',
    color: colors.text,
    height: 'calc(var(--person-id-field-height, 132px) - 48px) !important',
    minHeight: '84px !important',
    maxHeight: 'none',
    overflowY: 'auto !important',
    resize: 'none',
    boxSizing: 'border-box',
    scrollbarWidth: 'thin',
    scrollbarColor: `${colors.borderLight} transparent`,
    '&::-webkit-scrollbar': {
      width: '10px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: colors.borderLight,
      borderRadius: '999px',
      border: '3px solid transparent',
      backgroundClip: 'content-box',
      minHeight: '56px',
    },
  },
};

// Grays out a population Select while keeping it interactive (click activates the option).
export const inactivePopulationSelectSx = {
  '& .MuiSelect-select.MuiSelect-select': {
    color: colors.disabledText,
    WebkitTextFillColor: colors.disabledText,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0, 0, 0, 0.26)',
  },
  '& .MuiSelect-icon': {
    color: colors.disabledText,
  },
};

// Grays out the Person ID field while keeping it interactive (focus activates the option).
export const inactivePersonIdFieldSx = {
  '& .MuiInputBase-inputMultiline': {
    color: colors.disabledText,
    WebkitTextFillColor: colors.disabledText,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0, 0, 0, 0.26)',
  },
};

export const getDropdownFieldSx = (borderColor) => ({
  ...sharedFieldSx,
  '& .MuiOutlinedInput-root': {
    ...sharedFieldSx['& .MuiOutlinedInput-root'],
    borderRadius: fieldBorderRadius,
    overflow: 'hidden',
    '& .MuiOutlinedInput-notchedOutline': {
      ...sharedFieldSx['& .MuiOutlinedInput-root']['& .MuiOutlinedInput-notchedOutline'],
      borderColor,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor,
      borderWidth: drawerBorderWidth,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.primary,
      borderWidth: drawerBorderWidth,
    },
  },
  '& .MuiAutocomplete-inputRoot, & .MuiAutocomplete-inputRoot.MuiOutlinedInput-root': {
    borderRadius: fieldBorderRadius,
    padding: '0px 52px 0px 16px !important',
    height: '44px',
  },
  '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
    padding: '0 !important',
  },
  '& .MuiAutocomplete-endAdornment, & .MuiInputAdornment-root': {
    ...lockedDateControlSx,
  },
  '& .MuiAutocomplete-popupIndicator': {
    color: colors.text,
    padding: 0,
    marginRight: '4px',
  },
});

export const getDropdownPaperSx = (menuOpen, borderColor) => ({
  mt: 0,
  border: `1px solid ${borderColor}`,
  borderRadius: fieldBorderRadius,
  boxShadow: '0 8px 8px 0 rgba(0, 0, 0, 0.10)',
  overflow: 'hidden',
  '& .MuiAutocomplete-listbox': {
    padding: '8px 0',
    scrollbarWidth: 'auto',
    scrollbarColor: `${colors.borderLight} transparent`,
  },
});

export const getDatePartSelectSx = (hasError = false) => {
  const outlineSx = {
    ...compactFieldSx['& .MuiOutlinedInput-root']['& .MuiOutlinedInput-notchedOutline'],
    borderColor: hasError ? colors.error : colors.border,
    borderWidth: hasError ? 2 : drawerBorderWidth,
  };

  const selectRootSx = {
    ...compactFieldSx['& .MuiOutlinedInput-root'],
    height: 44,
    minHeight: 44,
    padding: 0,
    borderRadius: fieldBorderRadius,
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
    alignItems: 'center',
    '& .MuiOutlinedInput-notchedOutline': outlineSx,
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? colors.error : colors.border,
      borderWidth: hasError ? 2 : drawerBorderWidth,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? colors.error : colors.primary,
      borderWidth: hasError ? 2 : drawerBorderWidth,
    },
  };

  return {
    ...compactFieldSx,
    borderRadius: fieldBorderRadius,
    '&.MuiOutlinedInput-root': selectRootSx,
    '&.MuiOutlinedInput-root::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: lockedDateControlSx.width,
      bottom: 0,
      borderLeft: '1px solid ' + colors.border,
      pointerEvents: 'none',
    },
    '& .MuiOutlinedInput-root': selectRootSx,
    '& .MuiSelect-select': {
      padding: '10px 32px 10px 16px !important',
      minWidth: 0,
      maxWidth: '100%',
      boxSizing: 'border-box',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontFamily: 'Noto Sans',
      fontSize: 14,
      lineHeight: '24px',
      letterSpacing: '0%',
      color: colors.text,
      minHeight: '24px !important',
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiSelect-icon': {
      right: 8,
      color: colors.iconFill,
      fontSize: 24,
    },
    '& .MuiAutocomplete-inputRoot, & .MuiAutocomplete-inputRoot.MuiOutlinedInput-root': {
      ...selectRootSx,
      padding: '0 32px 0 16px !important',
    },
    '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
      height: '24px !important',
      padding: '0 !important',
      fontFamily: 'Noto Sans',
      fontSize: 14,
      lineHeight: '24px',
      color: colors.text,
      minWidth: '0 !important',
    },
    '& .MuiAutocomplete-endAdornment': {
      ...lockedDateControlSx,
    },
    '& .MuiAutocomplete-popupIndicator': {
      color: colors.iconFill,
      padding: 0,
    },
  };
};

export const getTimeWindowSelectSx = () => getDatePartSelectSx(false);

export const getDateDropdownPaperSx = () => ({
  mt: 0,
  border: `1px solid ${colors.primary}`,
  borderRadius: fieldBorderRadius,
  boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.14)',
  overflow: 'hidden',
  '& .MuiAutocomplete-listbox': {
    maxHeight: 312,
    padding: '0 0 8px',
    scrollbarWidth: 'thin',
    scrollbarColor: `${colors.borderLight} transparent`,
  },
});

export const getDateDropdownOptionSx = (selected) => ({
  display: 'flex',
  padding: '16px 24px 16px 16px',
  alignItems: 'center',
  alignSelf: 'stretch',
  boxSizing: 'border-box',
  minHeight: 'auto',
  fontFamily: 'Noto Sans',
  fontSize: 14,
  fontWeight: selected ? 600 : 400,
  lineHeight: '150%',
  letterSpacing: '0%',
  color: selected ? `${colors.primary} !important` : `${colors.muted} !important`,
  backgroundColor: selected ? `${colors.optionSelected} !important` : '#FFFFFF',
  '&:hover, &.Mui-focused, &[data-focus="true"], &.Mui-focusVisible': {
    backgroundColor: selected ? `${colors.optionSelected} !important` : `${colors.optionHover} !important`,
    color: selected ? `${colors.primary} !important` : `${colors.muted} !important`,
  },
  '&&.Mui-selected, &&[aria-selected="true"]': {
    backgroundColor: `${colors.optionSelected} !important`,
    color: `${colors.primary} !important`,
    fontWeight: 600,
  },
  '&&.Mui-selected:hover, &&.Mui-selected.Mui-focusVisible, &&[aria-selected="true"]:hover, &&[aria-selected="true"].Mui-focused, &&[aria-selected="true"].Mui-focusVisible': {
    backgroundColor: `${colors.optionSelected} !important`,
    color: `${colors.primary} !important`,
  },
});

export const lockedDateFieldSx = {
  height: 44,
  minHeight: 44,
  width: '100%',
  border: `${drawerBorderWidth} solid ${colors.border}`,
  borderRadius: fieldBorderRadius,
  backgroundColor: '#F8F8F8',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  boxSizing: 'border-box',
};

export const lockedDateTextSx = {
  fontFamily: 'Noto Sans',
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '24px',
  color: colors.text,
  flex: 1,
  minWidth: 0,
  pl: 2,
  pr: 1,
};

export const lockedDateControlSx = {
  width:36,
  height: '100%',
  flexShrink: 0,
  borderLeft: '1px solid ' + colors.border,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  top: '0 !important',
  right: '0 !important',
  transform: 'none',
};

export const getTimeWindowMenuProps = (menuOpen) => ({
  PaperProps: {
    sx: {
      ...getDropdownPaperSx(menuOpen, colors.primary),
      boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.14)',
    },
  },
  MenuListProps: {
    sx: {
      py: 0,
      '& .MuiMenuItem-root': {
        display: 'flex',
        padding: '16px 24px',
        alignItems: 'center',
        gap: '8px',
        alignSelf: 'stretch',
        boxSizing: 'border-box',
        fontFamily: 'Noto Sans',
        fontSize: 14,
        fontWeight: 400,
        lineHeight: '150%',
        color: colors.text,
      },
      '& .MuiMenuItem-root.Mui-selected': {
        backgroundColor: `${colors.optionSelected} !important`,
        color: `${colors.primary} !important`,
        fontWeight: 700,
      },
      '& .MuiMenuItem-root.Mui-selected:hover': {
        backgroundColor: `${colors.optionSelected} !important`,
        color: `${colors.primary} !important`,
      },
      '& .MuiMenuItem-root:hover': {
        backgroundColor: colors.optionHover,
      },
      '& .MuiMenuItem-root.Mui-focusVisible, & .MuiMenuItem-root.Mui-focusVisible.Mui-selected': {
        backgroundColor: colors.optionHover,
      },
      '& .MuiMenuItem-root.Mui-selected.Mui-focusVisible': {
        backgroundColor: `${colors.optionSelected} !important`,
        color: `${colors.primary} !important`,
      },
    },
  },
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
  disablePortal: true,
});

export const filterSectionLabelSx = {
  fontFamily: 'Noto Sans',
  fontSize: 14,
  fontWeight: 600,
  lineHeight: '21px',
  letterSpacing: '0%',
  color: colors.text,
};

export const helperTextSx = {
  fontFamily: 'Noto Sans',
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '21px',
  letterSpacing: '0%',
  color: colors.text,
};

export const priorComparisonHelperTextSx = {
  ...helperTextSx,
  whiteSpace: 'nowrap',
};

const assistiveTextSx = {
  fontFamily: 'Noto Sans',
  fontSize: 12,
  fontWeight: 400,
  lineHeight: '16.8px',
  letterSpacing: '0%',
  color: colors.text,
};

export const dateInputLabelSx = {
  fontFamily: 'Noto Sans',
  fontSize: 12,
  fontWeight: 600,
  lineHeight: '16.8px',
  color: colors.text,
};

export const errorTextSx = {
  ...assistiveTextSx,
  fontWeight: 600,
  color: colors.error,
};

export const warningTextSx = {
  ...assistiveTextSx,
  flex: '1 0 0',
  color: colors.text,
};

export const personIdWarningTextSx = {
  ...assistiveTextSx,
  flex: '1 0 0',
  color: colors.muted,
  leadingTrim: 'both',
  textEdge: 'cap',
  fontStyle: 'normal',
};

export const dateBasisGuidanceTextSx = {
  ...assistiveTextSx,
  flex: '1 0 0',
};

export const optionLabelSx = {
  fontFamily: 'Noto Sans',
  fontSize: 14,
  fontWeight: 600,
  lineHeight: '21px',
  color: colors.text,
};

export const disabledOptionLabelSx = {
  color: colors.disabledText,
};

export const getDropdownOptionSx = (selected) => ({
  display: 'flex',
  padding: '16px 24px',
  alignItems: 'center',
  gap: '8px',
  alignSelf: 'stretch',
  boxSizing: 'border-box',
  minHeight: 'auto',
  fontFamily: 'Noto Sans',
  fontSize: 14,
  fontWeight: selected ? 600 : 400,
  lineHeight: '150%',
  letterSpacing: '0%',
  color: selected ? `${colors.primary} !important` : `${colors.text} !important`,
  backgroundColor: selected ? `${colors.optionSelected} !important` : '#FFFFFF',
  '&:hover, &.Mui-focused, &[data-focus="true"], &.Mui-focusVisible': {
    backgroundColor: selected ? `${colors.optionSelected} !important` : `${colors.optionHover} !important`,
    color: selected ? `${colors.primary} !important` : `${colors.text} !important`,
  },
  '&&.Mui-selected, &&[aria-selected="true"]': {
    backgroundColor: `${colors.optionSelected} !important`,
    color: `${colors.primary} !important`,
    fontWeight: 600,
  },
  '&&.Mui-selected:hover, &&.Mui-selected.Mui-focusVisible, &&[aria-selected="true"]:hover, &&[aria-selected="true"].Mui-focused, &&[aria-selected="true"].Mui-focusVisible': {
    backgroundColor: `${colors.optionSelected} !important`,
    color: `${colors.primary} !important`,
  },
});

export const getTimeWindowMenuItemSx = (selected, index, total) => ({
  ...getDropdownOptionSx(selected),
  '&&.MuiMenuItem-root': {
    display: 'flex',
    padding: '16px 24px',
    alignItems: 'center',
    gap: '8px',
    alignSelf: 'stretch',
    justifyContent: 'flex-start',
    width: '100%',
    minHeight: 'auto',
    boxSizing: 'border-box',
    fontFamily: 'Noto Sans',
    fontSize: 14,
    lineHeight: '150%',
    letterSpacing: '0%',
    whiteSpace: 'normal',
    borderTopLeftRadius: selected && index === 0 ? fieldBorderRadius : 0,
    borderTopRightRadius: selected && index === 0 ? fieldBorderRadius : 0,
    borderBottomLeftRadius: selected && index === total - 1 ? fieldBorderRadius : 0,
    borderBottomRightRadius: selected && index === total - 1 ? fieldBorderRadius : 0,
  },
  '&&.MuiMenuItem-root.Mui-selected': {
    backgroundColor: `${colors.optionSelected} !important`,
    color: `${colors.primary} !important`,
    fontWeight: 600,
  },
  '&&.MuiMenuItem-root.Mui-selected:hover, &&.MuiMenuItem-root.Mui-selected.Mui-focusVisible': {
    backgroundColor: `${colors.optionSelected} !important`,
    color: `${colors.primary} !important`,
  },
});

export const checkboxSx = {
  p: 0,
  '&.Mui-checked': {
    color: colors.primary,
  },
  '&.Mui-disabled': {
    color: colors.disabledText,
  },
  '& .MuiSvgIcon-root': {
    fontSize: 16,
  },
};

export const radioSx = {
  p: 0,
  '&.Mui-checked': {
    color: colors.primary,
  },
  '&.Mui-focusVisible': {
    borderRadius: '50%',
    color: colors.primary,
    outline: `2px solid ${colors.primary}`,
    outlineOffset: '2px',
  },
  '& .MuiSvgIcon-root': {
    fontSize: 16,
  },
};

export const radioOptionRowSx = {
  alignItems: 'center',
};

export const priorComparisonSwitchSx = {
  width: 40,
  height: 24,
  p: 0,
  '& .MuiSwitch-switchBase': {
    p: '3px',
    color: '#FFFFFF',
    '&.Mui-checked': {
      color: '#FFFFFF',
      transform: 'translateX(16px)',
      '& + .MuiSwitch-track': {
        backgroundColor: '#001A47',
        borderColor: '#001A47',
        opacity: 1,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 18,
    height: 18,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.20)',
  },
  '& .MuiSwitch-track': {
    border: `1px solid ${colors.dividerStroke}`,
    borderRadius: '12px',
    backgroundColor: colors.dividerStroke,
    opacity: 1,
    boxSizing: 'border-box',
  },
};

export const selectionPanelSx = {
  width: '100%',
  border: `${drawerBorderWidth} solid ${colors.border}`,
  borderRadius: fieldBorderRadius,
  backgroundColor: '#FFFFFF',
  p: 3,
};

const dateRangePanelSx = {
  ...selectionPanelSx,
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
};

export const getDateRangePanelSx = (hasError) => ({
  ...dateRangePanelSx,
  border: `${hasError ? '2px' : drawerBorderWidth} solid ${hasError ? colors.error : colors.border}`,
});

export const getErrorFieldSx = (baseSx) => ({
  ...baseSx,
  '& .MuiOutlinedInput-root': {
    ...baseSx['& .MuiOutlinedInput-root'],
    '& .MuiOutlinedInput-notchedOutline': {
      ...baseSx['& .MuiOutlinedInput-root']['& .MuiOutlinedInput-notchedOutline'],
      borderColor: colors.error,
      borderWidth: 2,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.error,
      borderWidth: 2,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.error,
      borderWidth: 2,
    },
  },
  '& .MuiOutlinedInput-root fieldset': {
    borderRadius: fieldBorderRadius,
    borderColor: `${colors.error} !important`,
    borderWidth: '2px !important',
  },
});

export const getSelectionPanelSx = (hasError) => ({
  ...selectionPanelSx,
  border: `${hasError ? '2px' : drawerBorderWidth} solid ${hasError ? colors.error : colors.border}`,
});

export const loadingPanelSx = {
  ...selectionPanelSx,
  minHeight: 264,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export const checklistSearchFieldSx = {
  ...compactFieldSx,
  '& .MuiOutlinedInput-root': {
    ...compactFieldSx['& .MuiOutlinedInput-root'],
    minHeight: 40,
    gap: '8px',
  },
  '& .MuiInputBase-input::placeholder': {
    color: colors.disabledText,
    opacity: 1,
  },
};

export const checklistEmptyStateSx = {
  ...assistiveTextSx,
  fontSize: 14,
  lineHeight: '21px',
  color: colors.disabledText,
};

export const getResetButtonSx = (personIdValue) => ({
  width: '53px',
  height: '28px',
  minHeight: '28px',
  minWidth: 'auto',
  padding: '8px 10px',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '5px',
  whiteSpace: 'nowrap',
  textTransform: 'none',
  borderRadius: '24px',
  border: `1px solid ${colors.primary}`,
  backgroundColor: '#FFF',
  fontFamily: 'Noto Sans',
  fontWeight: 600,
  fontSize: '12px',
  lineHeight: '140%',
  letterSpacing: '0%',
  color: colors.primary,
  visibility: personIdValue ? 'visible' : 'hidden',
  '&:hover': {
  backgroundColor: '#FFF',
  borderColor: colors.primary,
  },
});

export const buttonOutlinedSx = {
  alignSelf: 'flex-start',
  borderRadius: '24px',
  textTransform: 'none',
  fontFamily: 'Noto Sans',
  fontWeight: 600,
  fontSize: 16,
  letterSpacing: '0%',
  lineHeight: '14px',
  py: 2,
  px: 2,
  minHeight: 40,
  color: colors.primary,
  borderColor: colors.primary,
  boxShadow: 'none',
  outline: 'none',
  '&:hover': {
    borderColor: colors.primary
  },
  '&:focus': {
    borderColor: colors.primary,
    boxShadow: 'none'
  },
  '&:focus-visible': {
    borderColor: colors.primary,
    boxShadow: `0 0 0 2px #FFFFFF, 0 0 0 4px ${colors.primary}`
  },
  '&.Mui-focusVisible': {
    borderColor: colors.primary,
    boxShadow: `0 0 0 2px #FFFFFF, 0 0 0 4px ${colors.primary}`
  }
};

export const buttonOpenCohortBuilderdSx = {
  alignSelf: 'flex-start',
  borderRadius: '24px',
  textTransform: 'none',
  fontFamily: 'Noto Sans',
  fontWeight: 600,
  fontSize: 14,
  letterSpacing: '0%',
  lineHeight: '18px',
  py: '8px',
  px: '12px',
  color: colors.primary,
  borderColor: colors.primary,
  borderWidth: '1px',
  boxShadow: 'none',
  outline: 'none',
  '&:hover': {
    borderColor: colors.primary
  },
  '&:focus': {
    borderColor: colors.primary,
    boxShadow: 'none'
  },
  '&:focus-visible': {
    borderColor: colors.primary,
    boxShadow: `0 0 0 2px #FFFFFF, 0 0 0 4px ${colors.primary}`
  },
  '&.Mui-focusVisible': {
    borderColor: colors.primary,
    boxShadow: `0 0 0 2px #FFFFFF, 0 0 0 4px ${colors.primary}`
  }
};

export const buttonContainedSx = {
  borderRadius: '24px',
  textTransform: 'none',
  fontFamily: 'Noto Sans',
  fontWeight: 600,
  fontSize: 16,
  lineHeight: '24px',
  py: 1.25,
  px: 2,
  minHeight: 40,
  backgroundColor: colors.primary,
  color: '#FFFFFF',
  boxShadow: 'none',
  outline: 'none',
  '&:hover': {
    backgroundColor: colors.primary,
    boxShadow: 'none'
  },
  '&:focus': {
    backgroundColor: colors.primary,
    color: '#FFFFFF',
    boxShadow: 'none'
  },
  '&:focus-visible': {
    backgroundColor: colors.primary,
    color: '#FFFFFF',
    boxShadow: `0 0 0 2px #FFFFFF, 0 0 0 4px ${colors.primary}`
  },
  '&.Mui-focusVisible': {
    backgroundColor: colors.primary,
    color: '#FFFFFF',
    boxShadow: `0 0 0 2px #FFFFFF, 0 0 0 4px ${colors.primary}`
  },
  '&:active': {
    backgroundColor: colors.primary,
    color: '#FFFFFF',
    boxShadow: 'none'
  }
  };

export const getCohortMenuProps = (menuOpen, anchorWidth) => ({
  ...getTimeWindowMenuProps(menuOpen),
  PaperProps: {
    sx: {
      ...getTimeWindowMenuProps(menuOpen).PaperProps.sx,
      mt: '-0.5px',
      ...(anchorWidth ? {
        width: anchorWidth,
        minWidth: `${anchorWidth}px !important`,
        maxWidth: anchorWidth,
      } : {}),
    },
  },
  MenuListProps: {
    sx: {
      ...getTimeWindowMenuProps(menuOpen).MenuListProps.sx,
      maxHeight: '312px',
      overflowY: 'auto',
      scrollbarWidth: 'thin',
      scrollbarColor: `${colors.borderLight} transparent`,
    },
  },
});

export const getCohortMenuItemSx = (selected, index, total) => ({
  ...getTimeWindowMenuItemSx(selected, index, total),
  textAlign: 'left',
  color: '#121A22 !important',
  padding: '16px 24px 16px 32px',
  minWidth: 0,
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  '&&.MuiMenuItem-root': {
    ...getTimeWindowMenuItemSx(selected, index, total)['&&.MuiMenuItem-root'],
    paddingLeft: '32px',
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
});

export const expandedHeaderSx = {
  fontFamily: 'Rubik',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: '600',
  lineHeight: '21px',
  color: colors.muted,
  letterSpacing: '0.14px',
  userSelect: 'none',
}

export const listSubHeaderSx = {
  pt: 2,
  pb: 1,
  ...expandedHeaderSx
};

export const collapsedHeaderSx = {
  ...expandedHeaderSx,
  fontFamily: 'Noto Sans',
  fontSize: '16px',
  fontWeight: '400',
  lineHeight: '24px'
}

export const cohortMenuItemRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  width: '100%',
  minWidth: 0,
};

export const cohortMenuItemLabelSx = {
  flex: 1,
  minWidth: 0,
  overflowWrap: 'anywhere',
  textAlign: 'left',
};

export const cohortEditIconButtonSx = {
  color: colors.primary,
  padding: '4px',
  flexShrink: 0,
  '&:hover': {
    backgroundColor: colors.optionHover,
  },
};

export const cohortDeleteIconButtonSx = {
  ...cohortEditIconButtonSx,
};

export const cohortSelectRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
  minWidth: 0,
};

export const cohortSelectFieldWrapperSx = {
  position: 'relative',
  flex: 1,
  minWidth: 0,
};

// Extra right padding on the select input so its text doesn't run under the
// in-field edit + delete icons.
export const cohortSelectWithEditIconSx = {
  '& .MuiSelect-select': {
    paddingRight: '100px !important',
  },
};

export const cohortInFieldEditIconButtonSx = {
  ...cohortEditIconButtonSx,
  position: 'absolute',
  top: 0,
  bottom: 0,
  right: '76px',
  margin: 'auto',
  height: '28px',
  zIndex: 2,
  backgroundColor: '#FFFFFF',
  '&:hover': {
    backgroundColor: colors.optionHover,
  },
};

export const cohortInFieldDeleteIconButtonSx = {
  ...cohortInFieldEditIconButtonSx,
  right: '44px',
};
